import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, X, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${window.location.origin}/api`;

export const Admin = () => {
  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const emptyProject = {
    title: "",
    category: "architecture",
    description: "",
    location: "",
    year: new Date().getFullYear().toString(),
    role: "",
    images: [""],
    is_visible: true
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const projectsRes = await axios.get(`${API}/projects?include_hidden=true`, { timeout: 30000 });
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Failed to load projects:", error);
    }
    try {
      const inquiriesRes = await axios.get(`${API}/contact`, { timeout: 30000 });
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error("Failed to load inquiries:", error);
    }
    try {
      const appointmentsRes = await axios.get(`${API}/appointments`, { timeout: 30000 });
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error("Failed to load appointments:", error);
    }
    setLoading(false);
  };

  const handleSaveProject = async (project) => {
    try {
      const cleanedProject = {
        ...project,
        images: project.images.filter(img => img.trim() !== "")
      };
      
      if (isCreating) {
        await axios.post(`${API}/projects`, cleanedProject);
        toast.success("Project created");
      } else {
        await axios.put(`${API}/projects/${project.id}`, cleanedProject);
        toast.success("Project updated");
      }
      setEditingProject(null);
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleToggleVisibility = async (project) => {
    try {
      await axios.put(`${API}/projects/${project.id}`, { is_visible: !project.is_visible });
      toast.success(project.is_visible ? "Project hidden" : "Project visible");
      fetchData();
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    try {
      await axios.delete(`${API}/contact/${inquiryId}`);
      toast.success("Inquiry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success("Appointment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  // Admin Panel
  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5F5F3]">
      {/* Header */}
      <header className="pt-12 pb-12 px-8 lg:px-24 border-b border-[#E0E0DE]">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Site
            </Link>
          </div>
          <h1 className="font-['Syne'] font-semibold text-4xl lg:text-5xl">Admin Panel</h1>
        </div>
      </header>

      {/* Content */}
      <section className="py-16 px-8 lg:px-24">
        <div className="max-w-screen-2xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <Tabs defaultValue="projects" className="w-full">
              <TabsList className="mb-8 bg-transparent border-b border-[#E0E0DE] rounded-none w-full justify-start gap-0 p-0 h-auto">
                <TabsTrigger 
                  value="projects" 
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0A0A0A] rounded-none px-6 py-4 text-sm uppercase tracking-wider"
                >
                  Projects ({projects.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="inquiries"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0A0A0A] rounded-none px-6 py-4 text-sm uppercase tracking-wider"
                >
                  Inquiries ({inquiries.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="appointments"
                  className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0A0A0A] rounded-none px-6 py-4 text-sm uppercase tracking-wider"
                >
                  Appointments ({appointments.length})
                </TabsTrigger>
              </TabsList>

              {/* Projects Tab */}
              <TabsContent value="projects">
                <div className="flex justify-end mb-8">
                  <button
                    onClick={() => { setEditingProject(emptyProject); setIsCreating(true); }}
                    className="btn-editorial"
                    data-testid="add-project-btn"
                  >
                    <Plus size={16} />
                    Add Project
                  </button>
                </div>

                <div className="space-y-4">
                  {projects.map((project) => (
                    <div 
                      key={project.id}
                      className={`flex items-center gap-6 p-6 border border-[#E0E0DE] bg-white ${!project.is_visible ? 'opacity-50' : ''}`}
                      data-testid={`admin-project-${project.id}`}
                    >
                      <div className="w-24 h-16 flex-shrink-0 overflow-hidden">
                        <img 
                          src={project.images[0]} 
                          alt={project.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-['Syne'] font-semibold text-lg truncate">{project.title}</h3>
                        <p className="text-sm text-[#737373]">{project.category} · {project.location} · {project.year}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleVisibility(project)}
                          className="p-2 hover:bg-[#E8E8E6] transition-colors"
                          title={project.is_visible ? "Hide" : "Show"}
                        >
                          {project.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        <button
                          onClick={() => { setEditingProject(project); setIsCreating(false); }}
                          className="p-2 hover:bg-[#E8E8E6] transition-colors"
                          data-testid={`edit-project-${project.id}`}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project.id)}
                          className="p-2 hover:bg-red-50 text-red-600 transition-colors"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Inquiries Tab */}
              <TabsContent value="inquiries">
                <div className="space-y-4">
                  {inquiries.length === 0 ? (
                    <p className="text-[#737373] text-center py-12">No inquiries yet</p>
                  ) : (
                    inquiries.map((inquiry) => (
                      <div 
                        key={inquiry.id}
                        className="p-6 border border-[#E0E0DE] bg-white"
                        data-testid={`admin-inquiry-${inquiry.id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">{inquiry.name}</h3>
                            <p className="text-sm text-[#737373]">{inquiry.email} · {inquiry.phone}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteInquiry(inquiry.id)}
                            className="p-2 hover:bg-red-50 text-red-600 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        <p className="text-sm font-medium mb-2">{inquiry.subject}</p>
                        <p className="text-sm text-[#737373]">{inquiry.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Appointments Tab */}
              <TabsContent value="appointments">
                <div className="space-y-4">
                  {appointments.length === 0 ? (
                    <p className="text-[#737373] text-center py-12">No appointments yet</p>
                  ) : (
                    appointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="p-6 border border-[#E0E0DE] bg-white"
                        data-testid={`admin-appointment-${apt.id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">{apt.name}</h3>
                            <p className="text-sm text-[#737373]">{apt.email} · {apt.phone}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-3 py-1 ${apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                              {apt.status}
                            </span>
                            <button
                              onClick={() => handleDeleteAppointment(apt.id)}
                              className="p-2 hover:bg-red-50 text-red-600 transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm"><strong>Date:</strong> {apt.preferred_date} at {apt.preferred_time}</p>
                        <p className="text-sm"><strong>Type:</strong> {apt.project_type}</p>
                        {apt.notes && <p className="text-sm text-[#737373] mt-2">{apt.notes}</p>}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {/* Edit/Create Project Modal */}
      <Dialog open={!!editingProject} onOpenChange={() => { setEditingProject(null); setIsCreating(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F5F5F3]">
          <DialogHeader>
            <DialogTitle className="font-['Syne'] text-2xl">
              {isCreating ? "Add New Project" : "Edit Project"}
            </DialogTitle>
          </DialogHeader>
          
          {editingProject && (
            <ProjectForm 
              project={editingProject}
              onSave={handleSaveProject}
              onCancel={() => { setEditingProject(null); setIsCreating(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Project Form Component
const ProjectForm = ({ project, onSave, onCancel }) => {
  const [form, setForm] = useState(project);
  const [uploading, setUploading] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...form.images];
    newImages[index] = value;
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const addImage = () => {
    setForm(prev => ({ ...prev, images: [...prev.images, ""] }));
  };

  const removeImage = (index) => {
    const newImages = form.images.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, images: newImages.length > 0 ? newImages : [""] }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newUrls = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/upload`, formData, { timeout: 60000 });
        const imageUrl = `${API}/files/${res.data.path}`;
        newUrls.push(imageUrl);
      } catch (err) {
        console.error("Upload error:", err);
        const detail = err?.response?.data?.detail || err?.message || "Network error";
        toast.error(`Failed: ${file.name} - ${detail}`);
      }
    }
    
    if (newUrls.length > 0) {
      setForm(prev => {
        const cleaned = prev.images.filter(img => img.trim() !== "");
        return { ...prev, images: [...cleaned, ...newUrls] };
      });
      toast.success(`${newUrls.length} image(s) uploaded`);
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-6 pt-4">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="form-label">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="Project title"
            data-testid="project-title-input"
          />
        </div>
        <div>
          <label className="form-label">Category *</label>
          <select
            value={form.category}
            onChange={(e) => handleChange("category", e.target.value)}
            data-testid="project-category-select"
          >
            <option value="architecture">Architecture</option>
            <option value="interior">Interior</option>
            <option value="furniture">Furniture</option>
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="form-label">Location *</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => handleChange("location", e.target.value)}
            placeholder="City, Country"
            data-testid="project-location-input"
          />
        </div>
        <div>
          <label className="form-label">Year *</label>
          <input
            type="text"
            value={form.year}
            onChange={(e) => handleChange("year", e.target.value)}
            placeholder="2024"
            data-testid="project-year-input"
          />
        </div>
      </div>

      <div>
        <label className="form-label">Role *</label>
        <input
          type="text"
          value={form.role}
          onChange={(e) => handleChange("role", e.target.value)}
          placeholder="e.g., Architect, Interior Designer"
          data-testid="project-role-input"
        />
      </div>

      <div>
        <label className="form-label">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={4}
          placeholder="Project description..."
          data-testid="project-description-input"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="form-label mb-0">Images *</label>
          <div className="flex items-center gap-3">
            <label 
              className="text-sm flex items-center gap-2 cursor-pointer bg-[#0A0A0A] text-white px-4 py-2 hover:bg-[#0A0A0A]/80 transition-colors"
              data-testid="upload-images-btn"
            >
              <Upload size={14} />
              {uploading ? "Uploading..." : "Upload Images"}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <button
              type="button"
              onClick={addImage}
              className="text-sm flex items-center gap-2 hover:opacity-60"
              data-testid="add-image-url-btn"
            >
              <Plus size={14} />
              Add URL
            </button>
          </div>
        </div>
        <div className="space-y-3">
          {form.images.map((img, index) => (
            <div key={index} className="flex gap-3">
              <input
                type="url"
                value={img}
                onChange={(e) => handleImageChange(index, e.target.value)}
                placeholder="https://... or upload above"
                className="flex-1"
                data-testid={`project-image-input-${index}`}
              />
              {form.images.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="p-2 hover:bg-red-50 text-red-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
        
        {/* Image Previews */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {form.images.filter(img => img.trim()).map((img, i) => (
            <img 
              key={i} 
              src={img} 
              alt={`Preview ${i + 1}`}
              className="w-24 h-16 object-cover flex-shrink-0 border border-[#E0E0DE]"
              onError={(e) => e.target.style.display = 'none'}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_visible"
          checked={form.is_visible}
          onChange={(e) => handleChange("is_visible", e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="is_visible" className="text-sm">Visible on website</label>
      </div>

      <div className="flex gap-4 pt-4 border-t border-[#E0E0DE]">
        <button
          type="button"
          onClick={() => onSave(form)}
          className="btn-editorial"
          data-testid="save-project-btn"
        >
          <Save size={16} />
          Save Project
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
